import React from "react";
import {
  FaEye,
  FaBullseye,
  FaCheckCircle,
  FaLeaf,
  FaHandsHelping,
  FaGraduationCap,
  FaUsers,
} from "react-icons/fa";

const VisionMissionEnhanced = () => {
  const visionPoints = [
    {
      title: "Dignity for All",
      text: "Empowering every individual with respect and opportunity.",
    },
    {
      title: "Harmony with Nature",
      text: "Ensuring children grow alongside a thriving environment.",
    },
    {
      title: "Knowledge & Self-Reliance",
      text: "Enlightening communities through education and skills.",
    },
    {
      title: "Economic Independence",
      text: "Bringing livelihoods to every doorstep.",
    },
    {
      title: "Compassion & Sustainability",
      text: "Uplifting the underserved through collective action.",
    },
  ];

  const missionPoints = [
    {
      icon: <FaHandsHelping />,
      title: "Economic Empowerment",
      text: "To provide marginalized women with the tools and training to build sustainable livelihoods, ensuring that their financial growth directly fuels their children's education and family health.",
    },
    {
      icon: <FaLeaf />,
      title: "Environmental Stewardship",
      text: "To pioneer a unique Life-Link plantation model, where every new birth is celebrated with trees that grow alongside the child, fostering a lifelong bond between humanity and the Earth.",
    },
    {
      icon: <FaUsers />,
      title: "Holistic Awareness",
      text: "To bridge the information gap in rural India by conducting high-impact camps on banking, health, government schemes, and civic rights.",
    },
    {
      icon: <FaGraduationCap />,
      title: "Future Readiness",
      text: "To equip the youth with essential life skills, from PoSH and self-defense to job readiness and civic responsibility, preparing them to lead with confidence and character.",
    },
  ];

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.7s ease-out forwards;
        }
      `}</style>

      <section className="relative overflow-hidden bg-gradient-to-br from-[#eafaff] via-white to-[#dff7fb] py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-10 left-0 w-72 h-72 bg-[#1298b5]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-0 w-80 h-80 bg-[#f97316]/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto flex flex-col gap-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-7 md:p-9 shadow-xl hover:shadow-2xl transition-all duration-300 border border-[#d8f4f8] animate-fadeInUp">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1298b5] to-[#0b819b] rounded-2xl flex items-center justify-center text-white text-2xl shadow-md">
                <FaEye />
              </div>

              <div>
                <span className="text-sm font-semibold text-[#1298b5]">
                  Our Direction
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#073b4c]">
                  Vision<span className="text-[#f97316]">.</span>
                </h2>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-7 text-base md:text-lg border-l-4 border-[#1298b5] pl-4">
              “To cultivate a society where every individual is empowered with
              dignity, every child grows in harmony with nature, and every
              community is enlightened through knowledge and self-reliance. We
              envision an India where economic independence begins at the
              doorstep, where environmental legacy is a birthright, and where
              informed citizenship is the cornerstone of a thriving nation.”
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visionPoints.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#f8fdfe] border border-[#d8f4f8] rounded-2xl p-4 hover:border-[#1298b5]/50 hover:shadow-md transition"
                >
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#1298b5] mt-1 flex-shrink-0" />

                    <div>
                      <h3 className="font-bold text-[#073b4c] mb-1">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="bg-[#073b4c] rounded-3xl p-7 md:p-9 shadow-xl hover:shadow-2xl transition-all duration-300 border border-[#1298b5]/30 animate-fadeInUp"
            style={{ animationDelay: "0.2s" }}
          >
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 bg-gradient-to-br from-[#f97316] to-[#fb923c] rounded-2xl flex items-center justify-center text-white text-2xl shadow-md">
                <FaBullseye />
              </div>

              <div>
                <span className="text-sm font-semibold text-[#5ecde0]">
                  Our Action
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-white">
                  Mission<span className="text-[#f97316]">.</span>
                </h2>
              </div>
            </div>

            <p className="text-gray-200 leading-relaxed mb-7 text-base md:text-lg border-l-4 border-[#f97316] pl-4">
              Our mission is to transform lives through a four-fold approach to
              grassroots development:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {missionPoints.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 border border-white/10 rounded-2xl p-5 hover:bg-white/15 transition"
                >
                  <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center text-[#5ecde0] text-xl mb-4">
                    {item.icon}
                  </div>

                  <h3 className="font-bold text-white mb-2">
                    {item.title}
                  </h3>

                  <p className="text-gray-200 text-sm leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default VisionMissionEnhanced;